/**
 * Log Review chart + filter endpoints.
 *
 * Feeds the seven tiles on the main Log Review dashboard, plus the two
 * filter sources (the Division → Application → Category cascade in the top
 * bar and the drawer's risk / team / status facets) that all three Log
 * Review screens share.
 */

import { LOG_REVIEW_URL } from "../constants/index";
import { useAuthStore } from "../stores/authStore";
import type {
  AreaChartData,
  LogReviewFilterDivision,
  NonCompliantBreakdown,
  PieChartDatum,
  RankedBarDatum,
  Severity,
  TreeMapNode,
} from "../app/types/logReview";
import { logReviewGet, logReviewPost, toNumber } from "./logReviewClient";

/**
 * Shared payload for every Log Review chart endpoint — the applied top
 * filter selection (application codes + categories) scoped to a date
 * window. Dates are ISO local strings (`yyyy-MM-ddTHH:mm:ss.SSS`).
 */
export interface LogReviewChartFilters {
  appCode: string[];
  category: string[];
  startDate: string;
  endDate: string;
}

// Saturated hues for the "by Team" treemap. The tiles are outlined rather
// than filled, so this colour is the stroke, the glow and the label -- the
// soft tints this used to carry were built to sit BEHIND dark text and read
// as washed-out wire on an unfilled tile.
const TEAM_HEATMAP_COLORS = ["#22c55e", "#8b5cf6", "#ef4444", "#f59e0b", "#06b6d4"];
// Ranked / lollipop accent palette — red, blue, purple, green, orange.
const RANK_COLORS = ["#ef4444", "#3b82f6", "#8b5cf6", "#22c55e", "#f59e0b"];

interface CountDetail {
  count: number;
  percentage: number;
}

// ─── Table filter options (risk / teams / status) — shared by all tabs ───

export interface LogReviewFilterOptions {
  risk: string[];
  teams: string[];
  status: string[];
}

export const fetchFilterOptions = (
  signal?: AbortSignal,
): Promise<LogReviewFilterOptions> =>
  logReviewGet<LogReviewFilterOptions>(
    `${LOG_REVIEW_URL.SIRAR}/listOfFilterOptions`,
    signal,
  );

// ─── Main filter cascade (Division → Application → Category) ─────────────

interface RawSirarFilter {
  divisions?: LogReviewFilterDivision[];
}

/**
 * Top filter-bar cascade shared by all three Log Review dashboards (main,
 * Reviewer Insights, CISO Log Review). Scoped to the signed-in user's
 * organization unless an explicit id is passed. The response is an array of
 * `{ divisions: [...] }` buckets — flatten them into a single division list.
 */
export const fetchSirarFilterTree = async (
  organizationId?: string | null,
  signal?: AbortSignal,
): Promise<LogReviewFilterDivision[]> => {
  const orgId = organizationId ?? useAuthStore.getState().user?.orgId ?? null;
  const path = orgId
    ? `${LOG_REVIEW_URL.FILTER_TREE}?organizationId=${encodeURIComponent(orgId)}`
    : LOG_REVIEW_URL.FILTER_TREE;
  const raw = await logReviewGet<RawSirarFilter[]>(path, signal);
  return (raw ?? [])
    .flatMap((r) => r.divisions ?? [])
    .map((d) => ({
      businessDivision: d.businessDivision ?? "",
      applications: (d.applications ?? []).map((a) => ({
        id: a.id,
        appCode: a.appCode,
        appName: a.appName,
        categories: a.categories ?? [],
      })),
    }));
};

// ─── #1 Compliance Status Overview (donut) ───────────────────────────────

interface RawComplianceStatusResponse {
  totalCount: number;
  details: Array<CountDetail & { complianceType: string }>;
}

// Fixed legend / slice order for the Compliance Status donut, regardless
// of the order the backend returns the buckets in:
//   Non-Compliant → Non-Compliant (Acknowledged) → Compliant.
const complianceRank = (name: string): number => {
  const n = name.toLowerCase();
  if (n.includes("ack")) return 1;
  if (n.includes("non") && n.includes("compliant")) return 0;
  return 2;
};

export const fetchComplianceStatus = async (
  filters: LogReviewChartFilters,
  signal?: AbortSignal,
): Promise<PieChartDatum[]> => {
  const raw = await logReviewPost<RawComplianceStatusResponse>(
    `${LOG_REVIEW_URL.SIRAR}/getComplianceStatusCount`,
    filters,
    signal,
  );
  return (raw.details ?? [])
    .map((d) => ({
      // The backend ships "Non Compliant(Acknowledged)" with no space
      // before the parenthesis — normalise for legend + tooltip display.
      name: (d.complianceType ?? "").replace(/\s*\(/g, " ("),
      value: toNumber(d.count),
    }))
    .sort((a, b) => complianceRank(a.name) - complianceRank(b.name));
};

// ─── #2 Non-Compliant Findings Distribution (severity breakdown) ─────────

interface RawSeverityCounts {
  nonCompliantCount: number;
  nonCompliantAckCount: number;
}

interface RawDistributionResponse {
  totalFindingsCount: number;
  totalFindings: Record<string, number>;
  lastMonthFindingsCount: number;
  lastMonthFindingPercentage: number;
  lastMonthFindings: Record<string, number>;
  lastMonthFindingsPercentage: Record<string, number>;
  high: RawSeverityCounts;
  medium: RawSeverityCounts;
  low: RawSeverityCounts;
}

// Safe lookup on a string-keyed numeric map — keys are fixed severity
// labels, not user input, so the dynamic access is not an injection sink.
const readNum = (rec: Record<string, number> | undefined, key: string): number => {
  const v = rec?.[key];
  return typeof v === "number" && Number.isFinite(v) ? v : 0;
};

export const fetchNonCompliantDistribution = async (
  filters: LogReviewChartFilters,
  signal?: AbortSignal,
): Promise<NonCompliantBreakdown> => {
  const raw = await logReviewPost<RawDistributionResponse>(
    `${LOG_REVIEW_URL.SIRAR}/getCombinedNonCompliantFindingsDistribution`,
    filters,
    signal,
  );
  const toSev = (
    severity: Severity,
    label: string,
    counts: RawSeverityCounts | undefined,
  ) => ({
    severity,
    label,
    nonCompliant: toNumber(counts?.nonCompliantCount),
    acknowledged: toNumber(counts?.nonCompliantAckCount),
    newCount: readNum(raw.lastMonthFindings, label),
    newPercent: readNum(raw.lastMonthFindingsPercentage, label),
  });
  return {
    total: toNumber(raw.totalFindingsCount),
    newCount: toNumber(raw.lastMonthFindingsCount),
    newPercent: toNumber(raw.lastMonthFindingPercentage),
    severities: [
      toSev("high", "High", raw.high),
      toSev("medium", "Medium", raw.medium),
      toSev("low", "Low", raw.low),
    ],
  };
};

// ─── Findings Trend (stacked area, 4 series) ─────────────────────────────

const TREND_MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

/**
 * Backend sends `updatedDate` as `DD-MM-YYYY, HH:mm`. Render the trend
 * x-axis tick as `DD Month` (e.g. `27 May`). Falls back to the raw value
 * if it doesn't match the expected shape.
 */
const formatTrendDate = (raw: string): string => {
  const datePart = (raw ?? "").split(",")[0]?.trim() ?? "";
  const [dd, mm] = datePart.split("-");
  const day = Number(dd);
  const monthIdx = Number(mm) - 1;
  if (!Number.isFinite(day) || day < 1 || monthIdx < 0 || monthIdx > 11) {
    return raw;
  }
  return `${String(day).padStart(2, "0")} ${TREND_MONTHS[monthIdx]}`;
};

interface RawFindingsTrendItem {
  updatedDate: string;
  totalNonCompliant: number;
  totalCompliant: number;
  totalNonCompliantAcknowledge: number;
  totalCount: number;
}

export const fetchFindingsTrend = async (
  filters: LogReviewChartFilters,
  signal?: AbortSignal,
): Promise<AreaChartData> => {
  const raw = await logReviewPost<RawFindingsTrendItem[]>(
    `${LOG_REVIEW_URL.SIRAR}/getFindingsTrendChart`,
    filters,
    signal,
  );
  const rows = raw ?? [];
  return {
    categories: rows.map((r) => formatTrendDate(r.updatedDate)),
    series: [
      { name: "Total Findings", data: rows.map((r) => toNumber(r.totalCount)), color: "#6366f1" },
      { name: "Non-Compliant Findings", data: rows.map((r) => toNumber(r.totalNonCompliant)), color: "#ef4444" },
      { name: "Non-Compliant (A) Findings", data: rows.map((r) => toNumber(r.totalNonCompliantAcknowledge)), color: "#fbbf24" },
      { name: "Compliant", data: rows.map((r) => toNumber(r.totalCompliant)), color: "#22c55e" },
    ],
  };
};

// ─── #3 Non-Compliant Workflow Status (donut) ────────────────────────────

interface RawWorkflowStatusResponse {
  totalCount: number;
  details: Array<CountDetail & { status: string }>;
}

const WORKFLOW_STATUS_ORDER = ["Open", "Pending Review", "Pending Approval"];

export const fetchNonCompliantWorkflowStatus = async (
  filters: LogReviewChartFilters,
  signal?: AbortSignal,
): Promise<PieChartDatum[]> => {
  const raw = await logReviewPost<RawWorkflowStatusResponse>(
    `${LOG_REVIEW_URL.SIRAR}/getNonCompliantFindingsByActionPlan`,
    filters,
    signal,
  );
  return (raw.details ?? [])
    .map((d) => ({ name: d.status, value: toNumber(d.count) }))
    .sort((a, b) => {
      const ai = WORKFLOW_STATUS_ORDER.indexOf(a.name);
      const bi = WORKFLOW_STATUS_ORDER.indexOf(b.name);
      return (ai === -1 ? Infinity : ai) - (bi === -1 ? Infinity : bi);
    });
};

// ─── #4 Non-Compliant Findings by Applications (Top-5) ───────────────────

interface RawByApplicationItem {
  applicationName: string;
  nonCompliantCount: number | string;
}

export const fetchNonCompliantByApplication = async (
  filters: LogReviewChartFilters,
  signal?: AbortSignal,
): Promise<RankedBarDatum[]> => {
  const raw = await logReviewPost<RawByApplicationItem[]>(
    `${LOG_REVIEW_URL.SIRAR}/getNonCompliantByApplication`,
    filters,
    signal,
  );
  return (raw ?? [])
    .map((row) => ({ name: row.applicationName, value: toNumber(row.nonCompliantCount) }))
    .sort((a, b) => b.value - a.value);
};

// ─── #5 Non-Compliant Findings By Team (Top-5) ───────────────────────────

interface RawByTeamItem {
  teamName: string;
  count: number | string;
}

export const fetchNonCompliantByTeam = async (
  filters: LogReviewChartFilters,
  signal?: AbortSignal,
): Promise<TreeMapNode[]> => {
  const raw = await logReviewPost<RawByTeamItem[]>(
    `${LOG_REVIEW_URL.SIRAR}/getNonComplianceTeam`,
    filters,
    signal,
  );
  return (raw ?? [])
    .map((row, i) => ({
      name: row.teamName,
      value: toNumber(row.count),
      color: TEAM_HEATMAP_COLORS[i % TEAM_HEATMAP_COLORS.length],
    }))
    .sort((a, b) => (b.value ?? 0) - (a.value ?? 0));
};

// ─── #6 Non-Compliant Findings by CR Categories (Top-5) ──────────────────

interface RawByCrCategoryItem {
  categoryName: string;
  categoryCount: number | string;
}

export const fetchNonCompliantByCrCategory = async (
  filters: LogReviewChartFilters,
  signal?: AbortSignal,
): Promise<RankedBarDatum[]> => {
  const raw = await logReviewPost<RawByCrCategoryItem[]>(
    `${LOG_REVIEW_URL.SIRAR}/getNonCompliantByCRCategory`,
    filters,
    signal,
  );
  return (raw ?? [])
    .map((row, i) => ({
      name: row.categoryName,
      value: toNumber(row.categoryCount),
      color: RANK_COLORS[i % RANK_COLORS.length],
    }))
    .sort((a, b) => b.value - a.value);
};
